import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { GRIS } from "../data/gris";
import type { ChapterData } from "../data/content-types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CAPÍTULO 5 — "Nadie vio nada". Oficina apagada y fría (psicosocial). */
export class GrisScene extends WalkChapter {
  protected chapter: ChapterData = GRIS;
  protected grid = { w: 15, h: 11 };
  protected spawn = { x: 8, y: 9 };
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("gris");
  }

  protected floorKind(): P.TileKind {
    return "concrete"; // moqueta/suelo frío de oficina
  }

  // NPC de oficina (sin casco ni chaleco)
  protected buildInteractableArt(def: import("../core/types").InteractableDef): Phaser.GameObjects.GameObject[] {
    if (def.type === "npc") return [P.officeperson(this, { shirt: def.color })];
    return super.buildInteractableArt(def);
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x63656b);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0x7c7e84, 0x7c7e84, 0x5a5c62, 0x5a5c62, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.62 + over);
    // edificios grises, apenas alguna ventana encendida (cielo encapotado)
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.46;
    for (const [x, w, ht] of [[40, 130, 150], [200, 100, 110], [330, 160, 200], [520, 110, 130], [900, 140, 180], [1080, 160, 150], [1250, 90, 120]] as [number, number, number][]) {
      sk.fillStyle(0x565962, 0.95); sk.fillRect(x, base - ht, w, ht);
      for (let wy = base - ht + 10; wy < base - 8; wy += 15) for (let wx = x + 6; wx < x + w - 6; wx += 13) { sk.fillStyle(((wx * 7 + wy) % 17 === 0) ? 0xcfd0c0 : 0x4a4d54, 0.85); sk.fillRect(wx, wy, 7, 8); }
    }
    // lluvia tenue
    sk.lineStyle(1, 0x9a9ca2, 0.28);
    for (let i = 0; i < 60; i++) { const rx = (i * 137) % W, ry = (i * 89) % (base | 0); sk.beginPath(); sk.moveTo(rx, ry); sk.lineTo(rx - 4, ry + 12); sk.strokePath(); }
    // antepecho de oficina (pared baja); el skyline encapotado queda como ventanal
    this.buildWalls({ left: 0x565962, right: 0x676a72, height: 88, base: 0x474a50, texture: "plain" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };
    const station = (x: number, y: number) => { put(P.desk(this, 0x7a726a), x, y); put(P.officechair(this, 0x5a5f68), x - 0.1, y + 0.8); };
    station(2.6, 2.6); station(11.6, 2.6);
    station(2.6, 6.2); station(12.2, 6.4);
    station(2.6, 9.2);
    put(P.plant(this), 13.4, 4.4); // una sola planta
    put(P.locker(this), 13.4, 8.8);

    // --- mobiliario de oficina (gris, algo descuidado) ---
    put(P.filecabinet(this, 0x6a6d74), 7.0, 1.4); put(P.filecabinet(this, 0x5f636a), 7.7, 1.5);
    put(P.filecabinet(this, 0x6a6d74), 13.6, 2.0);
    put(P.watercooler(this), 1.4, 8.4);
    put(P.printer(this), 9.4, 1.7);
    put(P.desk(this, 0x726a62), 8.2, 4.0); put(P.officechair(this, 0x565663), 8.1, 4.8);

    // paneles de techo (algunos apagados: luz fría y desigual)
    for (const [lx, ly, on] of [[4, 3, 1], [9, 3, 0], [4, 7, 1], [10, 6.5, 0]] as [number, number, number][]) {
      const p = this.iso(lx, ly);
      if (on) { const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2; pg.fillStyle(0xdfe0d4, 0.09); pg.fillEllipse(p.sx, pcy, 70, 33); pg.fillStyle(0xeceee2, 0.08); pg.fillEllipse(p.sx, pcy, 40, 19); this.decor.push(pg); this.addPulse(pg, 0.24, 0.012, 0.78); }
      const panel = P.ceilingPanel(this, 150); panel.setPosition(p.sx, p.sy).setDepth(50000); if (!on) panel.setAlpha(0.4); this.decor.push(panel);
    }

    // papeles tirados (desorden) sobre el suelo
    const g = this.add.graphics().setDepth(-9500);
    for (const [x, y] of [[6.5, 6.5], [7.5, 5], [5.5, 7.5]] as [number, number][]) {
      const p = this.iso(x, y); const cx = p.sx, cy = p.sy + TILE_H / 2;
      g.fillStyle(0xe8e2d2, 0.8); g.fillRect(cx - 6, cy - 3, 11, 8); g.fillRect(cx + 2, cy + 1, 9, 7);
    }
    this.decor.push(g);
    // velo gris frío sobre toda la escena (estética apagada)
    const veil = this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x6a6a72, 0.18).setScrollFactor(0).setDepth(500);
    this.decor.push(veil);
    if (this.state.chapter("gris").done) this.onResolved();
  }

  protected onResolved(): void {
    // el gris se aclara un poco
    this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0xeaf5ee, 0.16).setScrollFactor(0).setDepth(501);
  }
}
